"use client";
import type { Voice } from "@mw/shared/payload-types";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { toast } from "@/app/_providers/toast";
import { AudioRecorder } from "@/app/_utilities/audioRecorder";
import { useTranslations } from "next-intl";
import { createContext, useEffect, useRef, useState } from "react";

import { Generate, Overview, Recording } from "./views";

const views = {
  overview: <Overview />,
  recording: <Recording />,
  generate: <Generate />,
};

interface ISTSContext {
  audioRecorderRef: React.MutableRefObject<AudioRecorder | null>;
  duration: null | number;
  file: File | null;
  helpText: string;
  setFile: (file: File | null) => void;
  setView: (view: "generate" | "overview" | "recording") => void;
  stsPrompts: {
    direction: string;
    text: string;
  }[];
  view: "generate" | "overview" | "recording";
  voices: Voice[];
}

export const STSContext = createContext<ISTSContext>({
  view: "overview",
  setView: () => {},
  voices: [],
  audioRecorderRef: { current: null },
  duration: 0,
  file: null,
  setFile: () => {},
  stsPrompts: [],
  helpText: "",
});

const MAX_RECORDING_DURATION = 20;

export default function STS({
  voices,
  stsPrompts,
  stsHelpText,
}: {
  stsHelpText?: null | string;
  stsPrompts: { direction: string; text: string }[];
  voices: Voice[];
}) {
  const [view, setView] = useState<"generate" | "overview" | "recording">("overview");
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<null | number>(0);
  const t = useTranslations("ClientKeys.SpeechToSpeech.widget");
  const audioRecorderRef = useRef(
    new AudioRecorder({
      maxDurationSecs: MAX_RECORDING_DURATION,
      onMaxDurationCb: () => {
        captureEvent(EventName.mw_sts_recording_max_duration_reached, {
          max_duration_seconds: MAX_RECORDING_DURATION,
        });
        toast.error("Max duration reached", {
          description: "You've reached the maximum recording duration for the Speech to Speech demo.",
        });
      },
      onUpdate: ({ recordedAudioDurationSecs }) => setDuration(recordedAudioDurationSecs),
    }),
  );

  useEffect(() => {
    return () => audioRecorderRef.current?.stopRecording();
  }, []);
  return (
    <STSContext.Provider
      value={{
        view,
        setView,
        voices,
        audioRecorderRef,
        duration,
        file,
        setFile,
        stsPrompts:
          stsPrompts && stsPrompts.length > 0
            ? stsPrompts
            : // @ts-expect-error
              Object.entries(t.raw("prompts")).map(([_, value]: [string, { direction: string; text: string }]) => ({
                direction: value.direction,
                text: value.text,
              })),
        helpText: stsHelpText ?? t("help_text"),
      }}
    >
      <div className='flex h-full w-full flex-col overflow-hidden'>{views[view]}</div>
    </STSContext.Provider>
  );
}
